import React from "react";
import { PolicyStudioSidebar } from "./PolicyStudioSidebar";
import { PolicyStudioViewer } from "./PolicyStudioViewer";
import { Toaster } from "react-hot-toast";
import { usePublicPolicyStudio } from "../../hooks/usePublicPolicyStudio";
import "./PolicyStudio.css";
import config from "environment";
import { ModalTextInput } from "./ModalTextInput";
import { ModalPolicyStudioWarning } from "./ModalPolicyStudioWarning";
import { ModalPolicyStudioGate } from "./ModalPolicyStudioGate";
import ModalConfirm from "./components/ModalConfirm";
import DrawerPolicyCompliance from "./DrawerPolicyCompliance";
import DrawerPolicyConflicts from "./DrawerPolicyConflicts";
import { ModalEmailVerify } from "./ModalEmailVerify";

const PublicPolicyStudio: React.FC = () => {
  const { state, actions } = usePublicPolicyStudio();

  if (!config.showPolicyStudio) {
    return null;
  }

  return (
    <div className="policy-studio light-mode">
      <div className="policy-studio__sidebar">
        <PolicyStudioSidebar
          isPublic={true}
          policyProjects={[]}
          policy={state.selectedPolicy}
          isLoading={state.isLoading}
          onSelectPolicy={actions.setSelectedPolicy}
          handlePolicyCreation={actions.createPolicy}
          handleAdjustmentUpdate={actions.updateFromAdjustment}
          handlePromptUpdate={actions.updateFromPrompt}
          handlePolicyTitleUpdate={actions.updateTitle}
          handleCreateFromLinkRecord={() => {}}
          handleDelete={() => {}}
          linkRecords={[]}
          isCreateModalOpen={state.isCreateModalOpen}
          handleAddNewClick={actions.handleAddNewClick}
          handleCloseCreateModal={actions.handleCloseCreateModal}
          isCreateFromFileModalOpen={state.isCreateFromFileModalOpen}
          handleCloseCreateFromFileModal={
            actions.handleCloseCreateFromFileModal
          }
          handleCreateFromFileClick={actions.handleCreateFromFileClick}
          handleUploadFile={actions.uploadFile}
          isStreaming={state.isStreaming}
        />
      </div>
      <div className="policy-studio__viewer">
        <PolicyStudioViewer
          isPublic={true}
          policy={state.selectedPolicy}
          isLoading={state.isLoading}
          handleAddNewClick={actions.handleAddNewClick}
          handleAddAsResource={() => {}}
          openConflictsDrawer={actions.openConflictsDrawer}
          openComplianceDrawer={actions.openComplianceDrawer}
          exportContentToPdf={actions.exportToPdf}
          updatePolicyContent={actions.updateContent}
        />
      </div>
      <ModalConfirm
        isOpen={state.isConfirmNewPolicyModalOpen}
        onClose={() => actions.handleCloseConfirmNewPolicyModal()}
        onContinue={actions.handleConfirmNewPolicy}
        title="Are you sure?"
        text="You're about to create a new policy. This will reset your policy and all your progress will be lost."
      />
      <ModalConfirm
        isOpen={state.isConfirmNewPolicyFromFileModalOpen}
        onClose={() => actions.handleCloseConfirmNewPolicyFromFileModal()}
        onContinue={actions.handleConfirmNewPolicyFromFile}
        title="Are you sure?"
        text="You're about to create a new policy from a file. This will reset your policy and all your progress will be lost."
      />
      <ModalEmailVerify
        isOpen={state.isEmailModalOpen}
        onClose={() => {}}
        onSubmit={actions.updateEmail}
      />
      <ModalPolicyStudioGate
        isOpen={state.isPolicyGateModalOpen}
        title="Okay let's have a chat!"
        body="You've reached the limit of your free trial. Please book a demo with our team to learn more about Winslow."
      />
      <ModalPolicyStudioGate
        isOpen={state.isPolicyStudioShutdownModalOpen}
        title="Oops!"
        body="The Winslow Policy Studio is currently in maintenance mode. Please check back soon."
      />
      <ModalPolicyStudioWarning
        isOpen={state.warningModalMessage !== ""}
        onClose={() => actions.handleCloseWarningModal()}
        value={state.warningModalMessage}
        canClose={true}
        escapeToClose={true}
        allowClickout={true}
      />
      <DrawerPolicyConflicts
        isOpen={state.isConflictDrawerOpen}
        onClose={actions.closeConflictsDrawer}
        linkRecords={state.linkRecords}
        handleCheckConflicts={() => {}}
        conflictsCheckResults={state.selectedPolicy?.conflicts}
        isLoading={state.drawerIsLoading}
        exportToPdf={() => {}}
        isDirty={false}
        isPublic={true}
      />
      <DrawerPolicyCompliance
        isOpen={state.isComplianceDrawerOpen}
        onClose={actions.closeComplianceDrawer}
        handleCheckCompliance={actions.checkCompliance}
        complianceCheckResults={state.selectedPolicy?.compliance}
        isLoading={state.drawerIsLoading}
        exportToPdf={actions.exportToPdf}
        isDirty={state.selectedPolicy?.complianceDirty || false}
        isPublic={true}
      />
      <Toaster />
    </div>
  );
};

export default PublicPolicyStudio;
